import { useCallback, useState } from 'react';

import type { TravelMode } from './travel';
import type { LegChoice, LegChoices } from './travelLegs';
import { useAccountStorage } from '../../shared/account/context';
import type { KeyValueStorage } from '../../shared/account/storage';

/**
 * Where a user has put each travel block, and how they said they were getting
 * there. Held in localStorage beside the two event lists.
 *
 * Keyed by day and event pair rather than by position, so a choice survives
 * everything except the events themselves changing: adding an unrelated event,
 * reordering the day, reloading, closing the tab. A leg whose events are no
 * longer both on the schedule simply stops being read — the entry is left in
 * place rather than pruned, so removing an event and adding it back restores
 * what the user had rather than silently resetting it.
 */

const KEY = 'mcw.travelLegs';

function read(storage: KeyValueStorage): LegChoices {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};

    // Written by an older build, or by hand. Take only what type-checks.
    const out: LegChoices = {};
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (!value || typeof value !== 'object') continue;
      const { mode, startMinutes } = value as LegChoice;
      const entry: LegChoice = {};
      if (typeof mode === 'string') entry.mode = mode as TravelMode;
      if (typeof startMinutes === 'number' && Number.isFinite(startMinutes)) {
        entry.startMinutes = startMinutes;
      }
      if (Object.keys(entry).length > 0) out[id] = entry;
    }
    return out;
  } catch {
    return {};
  }
}

function write(choices: LegChoices, storage: KeyValueStorage): void {
  try {
    storage.setItem(KEY, JSON.stringify(choices));
  } catch {
    // Site data blocked. The drags still work for this session.
  }
}

export interface LegChoiceStore {
  choices: LegChoices;
  setStart: (id: string, startMinutes: number) => void;
  setMode: (id: string, mode: TravelMode, startMinutes: number) => void;
  reset: (id: string) => void;
}

export function useLegChoices(): LegChoiceStore {
  /** localStorage, or the account once the user has one. */
  const storage = useAccountStorage();
  const [choices, setChoices] = useState<LegChoices>(() => read(storage));

  // The choices moved: start again from wherever they now live.
  const [source, setSource] = useState(storage);
  if (source !== storage) {
    setSource(storage);
    setChoices(read(storage));
  }

  const merge = useCallback((id: string, patch: LegChoice) => {
    setChoices((prev) => {
      const next = { ...prev, [id]: { ...prev[id], ...patch } };
      write(next, storage);
      return next;
    });
  }, [storage]);

  const setStart = useCallback((id: string, startMinutes: number) => merge(id, { startMinutes }), [merge]);

  /**
   * Mode and position move together. Changing mode changes how long the journey
   * takes, and the caller has already worked out where that leaves the block —
   * writing them separately would render one frame with the new length at the
   * old position.
   */
  const setMode = useCallback(
    (id: string, mode: TravelMode, startMinutes: number) => merge(id, { mode, startMinutes }),
    [merge],
  );

  const reset = useCallback((id: string) => {
    setChoices((prev) => {
      const next = { ...prev };
      delete next[id];
      write(next, storage);
      return next;
    });
  }, [storage]);

  return { choices, setStart, setMode, reset };
}
